import { useEffect, useLayoutEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight, Compass } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TOUR } from "@/constants/testIds";

// User-triggered coaching: a coach-mark overlay that highlights the real
// field/button plus a side help panel carrying the same step text. Never
// modal-blocking — the page stays interactive (overlay is pointer-events-none).
export function GuidedTour({ open, steps = [], title = "Guided tour", onClose }) {
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);

  useEffect(() => { if (open) setI(0); }, [open]);

  const step = steps[i];

  const measure = useCallback(() => {
    if (!step?.selector) { setRect(null); return; }
    const el = document.querySelector(step.selector);
    if (!el) { setRect(null); return; }
    el.scrollIntoView({ block: "center", behavior: "smooth" });
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height, bottom: r.bottom, right: r.right });
  }, [step]);

  useLayoutEffect(() => {
    if (!open) return;
    measure();
    const t = setTimeout(measure, 180);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [open, i, measure]);

  if (!open || !step) return null;

  const pad = 6;
  const hasRect = !!rect;
  const vw = window.innerWidth, vh = window.innerHeight;

  const cardStyle = { position: "fixed", zIndex: 130, width: 320, maxWidth: "90vw" };
  if (hasRect) {
    const below = rect.bottom + 12;
    const above = rect.top - 12;
    if (vh - rect.bottom > 230 || rect.top < 200) {
      cardStyle.top = Math.min(below, vh - 240);
    } else {
      cardStyle.top = Math.max(16, above - 210);
    }
    cardStyle.left = Math.min(Math.max(16, rect.left), vw - 336);
  } else {
    cardStyle.top = vh / 2 - 110;
    cardStyle.left = vw / 2 - 160;
  }

  const last = i === steps.length - 1;
  const next = () => (last ? onClose?.() : setI((v) => Math.min(v + 1, steps.length - 1)));
  const back = () => setI((v) => Math.max(0, v - 1));
  const dim = "fixed bg-forest-deep/55";

  return createPortal(
    <div className="pointer-events-none fixed inset-0 z-[120]" data-testid={TOUR.overlay}>
      {hasRect ? (
        <>
          <div className={dim} style={{ top: 0, left: 0, right: 0, height: Math.max(0, rect.top - pad) }} />
          <div className={dim} style={{ top: Math.max(0, rect.top - pad), left: 0, width: Math.max(0, rect.left - pad), height: rect.height + pad * 2 }} />
          <div className={dim} style={{ top: Math.max(0, rect.top - pad), left: rect.right + pad, right: 0, height: rect.height + pad * 2 }} />
          <div className={dim} style={{ top: rect.bottom + pad, left: 0, right: 0, bottom: 0 }} />
          <div className="fixed rounded-lg ring-2 ring-sage-accent" style={{ top: rect.top - pad, left: rect.left - pad, width: rect.width + pad * 2, height: rect.height + pad * 2 }} />
        </>
      ) : (
        <div className="fixed inset-0 bg-forest-deep/55" />
      )}

      <div style={cardStyle} className="pointer-events-auto rounded-xl border border-sage-light bg-white p-4 shadow-2xl" data-testid={TOUR.card}>
        <div className="mb-1 flex items-center justify-between">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-forest-mid">Step {i + 1} of {steps.length}</span>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid={TOUR.skip} aria-label="Skip tour"><X className="h-4 w-4" /></button>
        </div>
        <p className="font-heading text-sm font-bold text-forest-dark" data-testid={TOUR.title}>{step.title}</p>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{step.body}</p>
        <div className="mt-3 flex items-center justify-between">
          <button onClick={onClose} className="text-xs text-muted-foreground hover:underline">Skip</button>
          <div className="flex gap-2">
            {i > 0 && (
              <Button size="sm" variant="outline" className="h-8" onClick={back} data-testid={TOUR.back}>
                <ChevronLeft className="mr-1 h-3.5 w-3.5" /> Back
              </Button>
            )}
            <Button size="sm" className="h-8 bg-forest-mid hover:bg-forest-dark" onClick={next} data-testid={TOUR.next}>
              {last ? "Done" : "Next"}{!last && <ChevronRight className="ml-1 h-3.5 w-3.5" />}
            </Button>
          </div>
        </div>
      </div>

      <div className="pointer-events-auto fixed right-4 top-20 z-[125] hidden w-64 rounded-xl border border-sage-light bg-white/95 p-3 shadow-xl backdrop-blur lg:block" data-testid={TOUR.panel}>
        <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-forest-dark">
          <Compass className="h-4 w-4 text-sage-accent" /> {title}
        </div>
        <ol className="space-y-1">
          {steps.map((s, idx) => (
            <li key={idx}>
              <button onClick={() => setI(idx)} className={`flex w-full items-start gap-2 rounded-lg px-2 py-1.5 text-left text-xs transition-colors ${idx === i ? "bg-sage-surface font-semibold text-forest-dark" : "text-muted-foreground hover:bg-sage-surface/50"}`}>
                <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[9px] ${idx === i ? "bg-forest-mid text-white" : "bg-slate-200 text-slate-600"}`}>{idx + 1}</span>
                {s.title}
              </button>
            </li>
          ))}
        </ol>
      </div>
    </div>,
    document.body
  );
}
